/**
 * useDeliveryHistory Hook
 * 배달 내역 조회 및 관리를 위한 커스텀 훅
 *
 * @description
 * - 배달 내역 데이터 조회 및 페이징
 * - 필터링 (날짜, 협력사, 상태)
 * - 합계 계산 (전체/페이지별)
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    DeliveryHistoryFilter,
    DeliveryHistoryResponse,
    DeliveryHistory,
    PartnerOption,
    StatusOption,
    DeliveryHistoryApiResponse,
    DeliveryHistoryRawItem,
    PartnerRawItem,
    PartnerListApiResponse,
} from '../types/deliveryHistory.types';
import { deliveryHistoryApi } from '../api/deliveryHistoryApi';
import { useAsync } from '@/shared/hooks/useAsync';
import { getItem } from '@/shared/utils/storage';
import { loadFilters, saveFilters } from '@/shared/utils/filterStorage';
import { getDefaultDateRange } from '@/shared/utils/dateDefaultUtils';
import {
    extractArrayFromResponse,
    formatYmdToDate,
    toSafeNumber,
    toSafeString,
} from '@/shared/utils/apiResponseHelpers';
import { calculateDeliveryCountTotals, EMPTY_DELIVERY_COUNT_TOTALS } from '@/shared/utils/aggregationUtils';

// ============================================================
// 상수 정의
// ============================================================

/** 필터 저장소 키 */
const FILTER_STORAGE_KEY = 'deliveryHistoryFilters';

/** 페이지당 항목 수 */
const DEFAULT_PAGE_SIZE = 10;

/** 상태 정렬 우선순위 키워드 */
const STATUS_PRIORITY_KEYWORDS = ['운행중', '운행종료', '종료'] as const;

// ============================================================
// 헬퍼 함수
// ============================================================

/**
 * API 응답을 DeliveryHistory 배열로 변환
 */
const mapDeliveryHistoryData = (raw: DeliveryHistoryApiResponse): DeliveryHistory[] => {
    const list = extractArrayFromResponse<DeliveryHistoryRawItem>(raw as DeliveryHistoryRawItem[], 'delivery');

    return list.map(
        (item): DeliveryHistory => ({
            supplyId: toSafeString(item.supplyId ?? item.suppId),
            suppName: item.suppName ? toSafeString(item.suppName) : undefined,
            riderId: toSafeString(item.riderId),
            riderName: item.riderName ? toSafeString(item.riderName) : undefined,
            phoneNum: item.phoneNum ? toSafeString(item.phoneNum) : undefined,
            deliveryDate: formatYmdToDate(toSafeString(item.deliveryDate)),
            deliveryStatusName: item.deliveryStatusName ? toSafeString(item.deliveryStatusName) : undefined,
            finishCnt: toSafeNumber(item.finishCnt),
            rejectCnt: toSafeNumber(item.rejectCnt),
            assingCancelCnt: toSafeNumber(item.assingCancelCnt),
            deliCancelCnt: toSafeNumber(item.deliCancelCnt),
            morTimeCnt: toSafeNumber(item.morTimeCnt),
            afterNonCnt: toSafeNumber(item.afterNonCnt),
            nightCnt: toSafeNumber(item.nightCnt),
            nightNonCnt: toSafeNumber(item.nightNonCnt),
        })
    );
};

/**
 * API 응답에서 날짜 메타데이터 추출
 */
const extractDateMeta = (data: DeliveryHistoryApiResponse): { fromDate?: string; toDate?: string } => {
    if (Array.isArray(data)) return {};
    if (typeof data !== 'object' || data === null) return {};

    const { fromDate, toDate } = data as { fromDate?: string; toDate?: string };
    return { fromDate, toDate };
};

/**
 * 상태 코드 정렬 (운행중 → 운행종료 순서)
 */
const sortStatusCodes = (codes: StatusOption[]): StatusOption[] => {
    if (!codes?.length) return [];

    return [...codes].sort((a, b) => {
        for (const keyword of STATUS_PRIORITY_KEYWORDS) {
            const aHas = a.name.includes(keyword);
            const bHas = b.name.includes(keyword);
            if (aHas && !bHas) return -1;
            if (!aHas && bHas) return 1;
        }
        return a.name.localeCompare(b.name);
    });
};

/**
 * 협력사 목록 응답 정규화
 */
const normalizePartnerList = (response: PartnerListApiResponse): PartnerOption[] => {
    let list: PartnerRawItem[] = [];

    if (Array.isArray(response)) {
        list = response;
    } else if (typeof response === 'object' && response !== null) {
        list =
            (response as { suppliers?: PartnerRawItem[] }).suppliers ||
            (response as { partners?: PartnerRawItem[] }).partners ||
            (response as { data?: PartnerRawItem[] }).data ||
            [];
    }

    return list
        .filter((item): item is PartnerRawItem => !!(item?.suppId || item?.suppName))
        .map((item) => ({
            suppId: toSafeString(item.suppId),
            suppName: toSafeString(item.suppName),
        }));
};

// ============================================================
// Hook 정의
// ============================================================

export const useDeliveryHistory = () => {
    // 사용자 정보
    const userType = getItem<string>('USER_TYPE');
    const userId = getItem<string>('USER_ID');
    const isSupplierUser = userType === 'SUPPLIER' && !!userId;

    // 기본 날짜 범위
    const { startDate: defaultStartDate, endDate: defaultEndDate } = getDefaultDateRange();

    // 필터 상태
    const [filter, setFilter] = useState<DeliveryHistoryFilter>(() => {
        const saved = loadFilters<DeliveryHistoryFilter>(FILTER_STORAGE_KEY);
        return (
            saved || {
                startDate: defaultStartDate,
                endDate: defaultEndDate,
                status: '',
                partnerId: isSupplierUser ? userId : '',
            }
        );
    });

    // 검색 실행 시 사용된 필터 (날짜 전달용)
    const [searchedFilter, setSearchedFilter] = useState<DeliveryHistoryFilter | null>(null);

    // 페이지네이션
    const [page, setPage] = useState(1);
    const limit = DEFAULT_PAGE_SIZE;

    // 데이터 상태
    const [allData, setAllData] = useState<DeliveryHistory[]>([]);
    const [partners, setPartners] = useState<PartnerOption[]>([]);
    const [partnersLoading, setPartnersLoading] = useState(false);
    const [statusCodes, setStatusCodes] = useState<StatusOption[]>([]);
    const [statusLoading, setStatusLoading] = useState(false);

    // API 호출
    const fetchFunction = useMemo(() => () => deliveryHistoryApi.getDeliveryHistory(filter), [filter]);

    const { result, execute: fetchDeliveryHistory, isLoading } = useAsync<DeliveryHistoryApiResponse>(fetchFunction);

    const loading = isLoading;
    const error = result.status === 'error' ? result.error : undefined;

    // 상태 코드 조회
    const fetchStatusCodes = useCallback(async () => {
        setStatusLoading(true);
        try {
            const response = await deliveryHistoryApi.getStatusCodes();
            setStatusCodes(response || []);
        } catch {
            setStatusCodes([]);
        } finally {
            setStatusLoading(false);
        }
    }, []);

    // 협력사 목록 조회 (본사 사용자만)
    const fetchPartners = useCallback(async () => {
        if (userType !== 'HEADQUARTERS') return;

        setPartnersLoading(true);
        try {
            const response = await deliveryHistoryApi.getPartnerList();
            setPartners(normalizePartnerList(response));
        } catch {
            setPartners([]);
        } finally {
            setPartnersLoading(false);
        }
    }, [userType]);

    // 초기 마운트
    useEffect(() => {
        fetchStatusCodes();
        fetchPartners();

        const savedFilters = loadFilters<DeliveryHistoryFilter>(FILTER_STORAGE_KEY);
        if (savedFilters || filter.partnerId || filter.startDate || filter.endDate) {
            fetchDeliveryHistory();
            if (savedFilters) setSearchedFilter(savedFilters);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // API 응답 처리
    useEffect(() => {
        if (result.status !== 'success' || !result.data) return;

        setAllData(mapDeliveryHistoryData(result.data));

        // 백엔드 날짜를 필터에 매핑
        const { fromDate, toDate } = extractDateMeta(result.data);
        if (fromDate || toDate) {
            setFilter((prev) => ({
                ...prev,
                startDate: fromDate ? formatYmdToDate(fromDate) : prev.startDate,
                endDate: toDate ? formatYmdToDate(toDate) : prev.endDate,
            }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [result]);

    // 정렬된 상태 코드
    const sortedStatusCodes = useMemo(() => sortStatusCodes(statusCodes), [statusCodes]);

    // 페이지네이션된 데이터
    const deliveryHistoryData = useMemo((): DeliveryHistoryResponse => {
        if (!allData.length) {
            return { data: [], total: 0, page: 1, limit };
        }

        const startIndex = (page - 1) * limit;
        return {
            data: allData.slice(startIndex, startIndex + limit),
            total: allData.length,
            page,
            limit,
        };
    }, [allData, page, limit]);

    // 전체 데이터 합계
    const totals = useMemo(
        () => (allData.length ? calculateDeliveryCountTotals(allData) : EMPTY_DELIVERY_COUNT_TOTALS),
        [allData]
    );

    // 현재 페이지 합계
    const pageTotals = useMemo(
        () =>
            deliveryHistoryData.data.length
                ? calculateDeliveryCountTotals(deliveryHistoryData.data)
                : EMPTY_DELIVERY_COUNT_TOTALS,
        [deliveryHistoryData.data]
    );

    // 필터 변경
    const handleFilterChange = useCallback((newFilter: Partial<DeliveryHistoryFilter>) => {
        setFilter((prev) => ({ ...prev, ...newFilter }));
    }, []);

    // 검색 실행
    const handleSearch = useCallback(() => {
        setPage(1);
        fetchDeliveryHistory();
        saveFilters(FILTER_STORAGE_KEY, filter);
        setSearchedFilter({ ...filter });
    }, [filter, fetchDeliveryHistory]);

    // 필터 초기화
    const handleReset = useCallback(() => {
        const defaultFilter: DeliveryHistoryFilter = {
            startDate: defaultStartDate,
            endDate: defaultEndDate,
            status: '',
            partnerId: isSupplierUser ? userId || '' : '',
        };
        setFilter(defaultFilter);
        setPage(1);
        setSearchedFilter(null);
        saveFilters(FILTER_STORAGE_KEY, defaultFilter);
    }, [defaultStartDate, defaultEndDate, isSupplierUser, userId]);

    // 페이지 변경
    const handlePageChange = useCallback((newPage: number) => {
        setPage(newPage);
    }, []);

    // 운행 상태 렌더링
    const renderOperationStatus = useCallback((statusName?: string) => {
        const text = statusName?.trim() || '운행 종료';
        const isEnded = /종료/.test(text);
        return React.createElement(
            'span',
            {
                className: `status-chip ${isEnded ? 'ended' : 'active'}`,
            },
            text
        );
    }, []);

    return {
        deliveryHistoryData,
        loading,
        error,
        filter,
        searchedFilter,
        page,
        limit,
        totals,
        pageTotals,
        partners,
        partnersLoading,
        statusCodes: sortedStatusCodes,
        statusLoading,
        userType,
        handleFilterChange,
        handleSearch,
        handleReset,
        handlePageChange,
        renderOperationStatus,
        refetch: fetchDeliveryHistory,
    };
};
